import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtMs, fmtInt, now, clamp } from '@/utils/format';
import { qualityFromTime, scoreFromTime } from '@/utils/scoring';

interface Props {
  autoRun: boolean;
  autoNonce: number;
}

export function LargeListTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState<'virtual' | 'full'>('virtual');
  const [count, setCount] = useState(4000);
  const [filter, setFilter] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [metrics, setMetrics] = useState({
    initialRenderMs: null as number | null,
    ttiMs: null as number | null,
    lastFilterMs: null as number | null,
    lastScrollPaintMs: null as number | null,
    renders: 0,
    itemsRendered: 0,
  });

  const dataRef = useRef<{ id: number; name: string; value: number }[]>([]);
  const versionRef = useRef(0);
  const [version, setVersion] = useState(0);
  const mountStartRef = useRef(0);
  const pendingFilterStart = useRef<number | null>(null);
  const pendingScrollStart = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRaf = useRef(0);
  const autoDoneRef = useRef(false);

  const itemH = 42;
  const overscan = 12;

  const filtered = useMemo(() => {
    const src = dataRef.current;
    const f = filter.trim().toLowerCase();
    if (!running || !src || src.length === 0) return [];
    if (!f) return src;
    return src.filter((it) => it.name.indexOf(f) !== -1);
  }, [filter, running, version]);

  const visibleRange = useMemo(() => {
    if (mode !== 'virtual') return { start: 0, end: filtered.length };
    const height = containerRef.current ? containerRef.current.clientHeight : 520;
    const visibleCount = Math.ceil(height / itemH);
    const start = clamp(Math.floor(scrollTop / itemH) - overscan, 0, Math.max(0, filtered.length - 1));
    const end = clamp(start + visibleCount + overscan * 2, 0, filtered.length);
    return { start, end };
  }, [scrollTop, filtered.length, mode]);

  const visibleItems = useMemo(() => {
    if (!running) return [];
    if (mode === 'full') return filtered;
    return filtered.slice(visibleRange.start, visibleRange.end);
  }, [filtered, visibleRange.start, visibleRange.end, mode, running]);

  useEffect(() => {
    if (!running) return;
    setMetrics((m) => ({ ...m, renders: m.renders + 1, itemsRendered: visibleItems.length }));
  }, [visibleItems.length, running]);

  function buildData(n: number) {
    const out = new Array(n);
    for (let i = 0; i < n; i++) {
      out[i] = {
        id: i + 1,
        name: ('item ' + (i + 1) + ' • status=' + (i % 7) + ' • region=' + (i % 13)).toLowerCase(),
        value: ((i * 2654435761) % 1000000) / 10,
      };
    }
    return out;
  }

  function resetAll() {
    setRunning(false);
    dataRef.current = [];
    setFilter('');
    setScrollTop(0);
    setVersion(0);
    setMetrics({ initialRenderMs: null, ttiMs: null, lastFilterMs: null, lastScrollPaintMs: null, renders: 0, itemsRendered: 0 });
  }

  function start(overrideCount?: number) {
    resetAll();
    autoDoneRef.current = false;
    setRunning(true);
    const wanted = overrideCount ?? count;
    const n = clamp(parseInt(String(wanted), 10) || 4000, 2000, 5000);
    setCount(n);
    dataRef.current = buildData(n);
    mountStartRef.current = now();
    versionRef.current++;
    setVersion(versionRef.current);
    requestAnimationFrame(() => {
      if (containerRef.current) containerRef.current.scrollTop = 0;
    });
  }

  function stop() {
    setRunning(false);
    const s1 = scoreFromTime(metrics.initialRenderMs, 900, 1800);
    const s2 = scoreFromTime(metrics.lastFilterMs, 140, 350);
    const sc = Math.round(0.65 * s1 + 0.35 * s2);
    const testId: TestId = 'tv-list';
    reportResult(testId, {
      score: sc,
      primary: `Initial ${fmtMs(metrics.initialRenderMs)} / Filter ${fmtMs(metrics.lastFilterMs)}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) {
      autoDoneRef.current = true;
      onAutoDone(testId);
    }
  }

  useLayoutEffect(() => {
    if (!running) return;
    if (metrics.initialRenderMs == null && dataRef.current.length) {
      const elapsed = now() - mountStartRef.current;
      setMetrics((m) => (m.initialRenderMs == null ? { ...m, initialRenderMs: elapsed } : m));
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const tti = now() - mountStartRef.current;
          setMetrics((m) => (m.ttiMs == null ? { ...m, ttiMs: tti } : m));
        }),
      );
    }
  });

  useLayoutEffect(() => {
    if (!running) return;
    if (pendingFilterStart.current != null) {
      const dt = now() - pendingFilterStart.current;
      pendingFilterStart.current = null;
      setMetrics((m) => ({ ...m, lastFilterMs: dt }));
    }
  }, [filter, version, running]);

  useLayoutEffect(() => {
    if (!running) return;
    if (pendingScrollStart.current != null) {
      const dt = now() - pendingScrollStart.current;
      pendingScrollStart.current = null;
      setMetrics((m) => ({ ...m, lastScrollPaintMs: dt }));
    }
  }, [scrollTop, running]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    if (mode !== 'virtual') return;
    const st = e.currentTarget.scrollTop;
    if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
    pendingScrollStart.current = now();
    scrollRaf.current = requestAnimationFrame(() => setScrollTop(st));
  }

  // AUTO-RUN
  useEffect(() => {
    if (!autoRun) return;
    setMode('virtual');
    setCount(4000);
    setFilter('');
    setScrollTop(0);
    start(4000);

    const t1 = setTimeout(() => {
      pendingFilterStart.current = now();
      setFilter('region=3');
    }, 2000);
    const t2 = setTimeout(() => stop(), 5200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [autoRun, autoNonce]);

  const qRender = qualityFromTime(metrics.initialRenderMs, 900, 1800);
  const qFilter = qualityFromTime(metrics.lastFilterMs, 140, 350);

  return (
    <TestCard title={t('tv.list.title', language)} description={t('tv.list.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={() => start()} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={resetAll} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.mode', language)}</div>
          <select value={mode} onChange={(e) => setMode(e.target.value as 'virtual' | 'full')} disabled={running || autoRun} className="input">
            <option value="virtual">{t('opt.virtual', language)}</option>
            <option value="full">{t('opt.fullDom', language)}</option>
          </select>
        </div>
        <div className="field">
          <div className="label">{t('label.count', language)}</div>
          <input value={count} disabled={running || autoRun} onChange={(e) => setCount(Number(e.target.value))} inputMode="numeric" className="input w-24" />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('metric.initialRender', language)} value={fmtMs(metrics.initialRenderMs)} hint={t('hint.renderGood', language)} />
        <MetricBox label={t('metric.tti', language)} value={fmtMs(metrics.ttiMs)} hint={t('hint.ttiHigh', language)} />
        <MetricBox label={t('metric.filterRerender', language)} value={fmtMs(metrics.lastFilterMs)} hint={t('hint.filterGood', language)} />
        <MetricBox label={t('metric.itemsRendered', language)} value={metrics.itemsRendered ? fmtInt(metrics.itemsRendered) : '—'} />
      </div>

      <div className="h-px bg-border my-3.5" />

      <div className="flex flex-wrap gap-2.5 items-center">
        <div className="field flex-1">
          <div className="label">{t('label.filter', language)}</div>
          <input
            value={filter}
            disabled={!running || autoRun}
            placeholder="np. region=3"
            onChange={(e) => { pendingFilterStart.current = now(); setFilter(e.target.value.toLowerCase()); }}
            className="input"
          />
        </div>
        <div className="text-xs">
          <span className={`font-black ${qRender.cls}`}>Initial: {qRender.label}</span>
          {' · '}
          <span className={`font-black ${qFilter.cls}`}>Filter: {qFilter.label}</span>
        </div>
      </div>

      <div className="mt-3" />
      <div
        className="h-[56vh] min-h-[340px] border border-border rounded-2xl overflow-auto bg-black/20"
        ref={containerRef}
        onScroll={onScroll}
      >
        {!running ? (
          <div className="text-xs text-muted p-3">{autoRun ? t('status.autoRun', language) : t('status.pressStart', language)}</div>
        ) : mode === 'full' ? (
          <div>
            {visibleItems.map((it) => (
              <div key={it.id} className="h-[42px] flex items-center px-3 border-b border-white/5 text-[15px] gap-2.5">
                <div className="w-2.5 h-2.5 rounded-full bg-accent/80 shadow-[0_0_0_3px_rgba(100,181,255,0.15)] shrink-0" />
                <div>{it.name}</div>
                <div className="text-muted font-mono text-xs ml-auto">v={it.value.toFixed(1)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ position: 'relative', height: filtered.length * itemH + 'px' }}>
            <div style={{ position: 'absolute', top: visibleRange.start * itemH + 'px', left: 0, right: 0 }}>
              {visibleItems.map((it) => (
                <div key={it.id} className="h-[42px] flex items-center px-3 border-b border-white/5 text-[15px] gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-accent/80 shadow-[0_0_0_3px_rgba(100,181,255,0.15)] shrink-0" />
                  <div>{it.name}</div>
                  <div className="text-muted font-mono text-xs ml-auto">#{it.id}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TestCard>
  );
}
